import { TablelistProApi } from '@lib/api'
import { toSearchParams } from '@utils/object'

import { Venue, VenueDaySettings, VenueEvent } from 'types/model'
import { ListParams, ReadParams } from 'types/api/common'
import { InventoryItem } from 'types/model/inventory'
import { getDateFromNow } from '@utils/date-time'
import {
  VenueLayout,
  VenueLayoutSection,
  VenueLayoutTable,
} from 'types/model/venue-layout'
import { AppConfig } from 'types/model/appConfig'
import { FloorplanSchemaVersion } from 'shared'

function unwrapListResponse<T>(response: T[] | { data: T[] }): T[] {
  return Array.isArray(response) ? response : response.data
}

class VenueApi extends TablelistProApi<Venue> {
  constructor() {
    super('/v1/venues')
  }

  async readAppConfig(venueId: string, readParams: ReadParams = {}) {
    const searchParams = toSearchParams(readParams)
    const response = await this.get<AppConfig>(
      `${this.basePath}/${venueId}/app-config`,
      searchParams
    )
    return response
  }

  async listEvents(
    venueId: string,
    {
      limit = 25,
      skip = 0,
      sort = 'dateStart',
      dateStart = getDateFromNow().toISOString(),
      dateEnd = getDateFromNow({ days: 30 }).toISOString(),
      ...params
    }: ListParams<VenueEvent> = {}
  ) {
    const listParams = { limit, skip, sort, dateStart, dateEnd, ...params }
    const searchParams = toSearchParams(listParams)
    const response = await this.get<{ data: VenueEvent[]; count: number }>(
      `${this.basePath}/${venueId}/events`,
      searchParams
    )
    return response
  }

  async listDaySettings(venueId: string, params: ListParams<VenueDaySettings>) {
    const response = await this.get<VenueDaySettings>(
      `${this.basePath}/${venueId}/day-settings`,
      toSearchParams(params)
    )
    return response
  }

  async listInventory(
    venueId: string,
    {
      limit = 25,
      skip = 0,
      sort,
      dateStart = getDateFromNow().toISOString(),
      dateEnd = getDateFromNow({ days: 30 }).toISOString(),
      ...params
    }: ListParams<InventoryItem> = {}
  ) {
    const listParams = { limit, skip, sort, dateStart, dateEnd, ...params }
    const searchParams = toSearchParams(listParams)
    const response = await this.get<{ data: InventoryItem[]; count: number }>(
      `${this.basePath}/${venueId}/inventory`,
      searchParams
    )
    return response
  }

  async listLayouts(venueId: string, params: ListParams<VenueLayout> = {}) {
    const listParams = {
      ...params,
      floorplanSchemaVersion: FloorplanSchemaVersion.LEGACY,
    }
    const response = await this.get<{ data: VenueLayout[] }>(
      `${this.basePath}/${venueId}/layouts`,
      toSearchParams(listParams)
    )
    return unwrapListResponse(response)
  }

  async listSections(
    venueId: string,
    { limit = 400, ...params }: ListParams<VenueLayoutSection> = {}
  ) {
    const response = await this.get<{ data: VenueLayoutSection[] }>(
      `${this.basePath}/${venueId}/layout-sections`,
      toSearchParams({ limit, ...params })
    )
    return unwrapListResponse(response)
  }

  async listTables(
    venueId: string,
    { limit = 400, ...params }: ListParams<VenueLayoutTable> = {}
  ) {
    const response = await this.get<{ data: VenueLayoutTable[] }>(
      `${this.basePath}/${venueId}/layout-tables`,
      toSearchParams({ limit, ...params })
    )
    return unwrapListResponse(response)
  }

  async readEvent(
    venueId: string,
    eventId: string,
    readParams: ReadParams = {}
  ) {
    const searchParams = toSearchParams(readParams)
    const response = await this.get<VenueEvent>(
      `${this.basePath}/${venueId}/events/${eventId}`,
      searchParams
    )
    return response
  }

  async readDaySettings(
    venueId: string,
    dayId: string,
    params: ReadParams = {}
  ) {
    const response = await this.get<VenueDaySettings>(
      `${this.basePath}/${venueId}/day-settings/${dayId}`,
      toSearchParams(params)
    )
    return response
  }

  async readInventory(
    venueId: string,
    inventoryId: string,
    readParams: ReadParams = {}
  ) {
    const searchParams = toSearchParams(readParams)
    const response = await this.get<InventoryItem>(
      `${this.basePath}/${venueId}/inventory/${inventoryId}`,
      searchParams
    )
    return response
  }
}

export const venueApi = new VenueApi()
